import React, {FC, useEffect, useState} from "react"
import {Row} from "react-bootstrap"
import CopyToClipboard from "react-copy-to-clipboard"
import * as PropTypes from "prop-types"

import GenericModal from "@irati/ui/src/GenericModal"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import {CelebrationsReportCategory, CelebrationsReportDuration} from "@irati/types"

import Button from "@irati/ui/src/Button/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import authentication from "@irati/auth"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {getDomain} from "@irati/utils/links"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {Select} from "@irati/ui/src/FormItem"
import Loading from "@irati/ui/src/Loading"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import ModalWidget from "../ModalWidget"
import Region, {ALL_TEAMS} from "../../../../Reporting/Region"
import buildAvailableDatesSelectOptions from "../../../../Reporting/Celebrations/helpers/buildAvailableDatesSelectOptions"
import buildMonthAndDurationFields from "../../../../Reporting/Celebrations/helpers/buildMonthAndDurationFields"
import CelebratedUser from "./CelebratedUser"
import RecognitionChain from "./RecognitionChain"

interface Props {
  show: boolean
  rewardPoints: number
}

const RecognitionModal: FC<Props> = ({show, rewardPoints, onHide, formatMessage}, {apps}) => {
  const hasRewardPoints = rewardPoints > 0
  const [showTopCelebrators, setShowTopCelebrators] = useState(false)
  const [showRecognitionChain, setShowRecognitionChain] = useState(false)
  const [receivers, setReceivers] = useState([])
  const [givers, setGivers] = useState([])
  const [isCopied, setIsCopied] = useState(false)
  const [regionId, setRegionId] = useState<string>(ALL_TEAMS)
  const businessName = authentication.business().businessName
  const [duration, setDuration] = useState<string>(CelebrationsReportDuration.CelebrationsLast_12Months)
  const [giversLoad, setGiversLoad] = useState<Promise<any> | null>(null)
  const [receiversLoad, setReceiversLoad] = useState<Promise<any> | null>(null)
  const [timeRangeOptions, setTimeRangeOptions] = useState([])

  useEffect(() => {
    setGiversLoad(
      getCelebrators(CelebrationsReportCategory.RecognitionGiving).then((res) => {
        const newTimeRangeOptions = buildAvailableDatesSelectOptions(res.availableDates, formatMessage)
        setTimeRangeOptions(newTimeRangeOptions)
        setGivers(res.recognition.users)
      })
    )

    setReceiversLoad(
      getCelebrators(CelebrationsReportCategory.RecognitionReceiving).then((res) => {
        setReceivers(res.recognition.users)
      })
    )
  }, [regionId, duration])

  useEffect(() => {
    setIsCopied(false)
  }, [showTopCelebrators])

  const getCelebrators = (category: CelebrationsReportCategory) => {
    const monthAndDuration = buildMonthAndDurationFields(duration)
    return adminReportingService.getCelebrationsReport({
      businessId: authentication.business().uuid,
      regionIds: regionId !== ALL_TEAMS ? [regionId] : undefined,
      duration: monthAndDuration.duration,
      category,
      month: monthAndDuration.month,
      limit: 5
    })
  }

  const getTopCelebratorsCopyText = () => {
    return `${formatMessage(
      {
        id: "admin.components.dashboard.reporting.recognition.modal.topCelebrators.copyToClipboard.top"
      },
      {
        businessName
      }
    )}\n${
      givers.length &&
      `\n${formatMessage({id: "admin.components.dashboard.reporting.recognition.modal.topCelebrators.copyToClipboard.giving"})}`
    }
    ${givers.map(
      (giver, i) =>
        `\n${formatMessage(
          {id: "admin.components.dashboard.reporting.recognition.modal.topCelebrators.copyToClipboard.giver"},
          {position: i + 1, name: firstnameLastname(giver.user), value: giver.recognitionIds.length}
        )}`
    )}
    ${
      receivers.length &&
      `\n${formatMessage({id: "admin.components.dashboard.reporting.recognition.modal.topCelebrators.copyToClipboard.receiving"})}`
    }
    ${receivers.map(
      (receiver, i) =>
        `\n${formatMessage(
          {id: "admin.components.dashboard.reporting.recognition.modal.topCelebrators.copyToClipboard.receiver"},
          {position: i + 1, name: firstnameLastname(receiver.user), value: receiver.recognitionIds.length}
        )}`
    )}\n
    ${formatMessage(
      {id: "admin.components.dashboard.reporting.recognition.modal.topCelebrators.copyToClipboard.join"},
      {link: `${getDomain(authentication.business().subdomain)}/recognition/polls/create`}
    )}
    `
  }

  const updateRegion = (region: string) => {
    setGivers([])
    setReceivers([])
    setRegionId(region)
  }

  const handleHide = () => {
    setShowTopCelebrators(false)
    setShowRecognitionChain(false)
    onHide()
  }

  const handleShareClick = () => {
    adminDashboardTracking.recognitionActions("cta_shareRecognition")
    setShowTopCelebrators(true)
  }

  const handleRecognitionChainClick = () => {
    adminDashboardTracking.recognitionActions("cta_recognitionChain")
    setShowRecognitionChain(true)
  }

  const handleRegionUpdate = (updatedRegionId: string) => {
    const isFiltered = updatedRegionId !== ALL_TEAMS || duration !== CelebrationsReportDuration.CelebrationsLast_12Months
    adminDashboardTracking.recognitionActions("recognitionfilterCountry", isFiltered)
    updateRegion(updatedRegionId)
  }

  const handleDurationUpdate = (updatedDuration: CelebrationsReportDuration) => {
    const isFiltered = updatedDuration !== CelebrationsReportDuration.CelebrationsLast_12Months || regionId !== ALL_TEAMS
    adminDashboardTracking.recognitionActions("recognitionfilterDate", isFiltered)
    setDuration(updatedDuration)
  }

  const handleCopyClick = () => {
    adminDashboardTracking.recognitionActions("cta_recognitioncopyText")
    setIsCopied(true)
  }

  const modalTitle = hasRewardPoints
    ? "admin.components.dashboard.reporting.recognition.modal.rewardPoints.title"
    : "admin.components.dashboard.reporting.recognition.modal.noRewardPoints.title"
  const copyText = isCopied
    ? "admin.components.dashboard.reporting.costOfLiving.modal.copiedToClipboard"
    : "admin.components.dashboard.reporting.costOfLiving.modal.copyButton"

  return (
    <GenericModal hideActions show={show} customWidth={884} onHide={handleHide}>
      {showRecognitionChain && <RecognitionChain onClick={() => setShowRecognitionChain(false)} />}
      {showTopCelebrators && (
        <div>
          <H2 marginBottom={1} maxWidth="100%">
            <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.title" />
          </H2>
          <Body maxWidth="100%">
            <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.body" />
          </Body>
          <Flex justifyContent="flex-end" gap={1}>
            <Region value={regionId} onChange={handleRegionUpdate} />
            <Row>
              <Select
                id="adminDashboard_reporting_recognition_timePeriod"
                name="adminDashboard_reporting_recognition_timePeriod"
                options={timeRangeOptions}
                value={duration}
                onChange={handleDurationUpdate}
              />
            </Row>
          </Flex>
          <H4 textAlign="center" maxWidth="100%" marginBottom={1}>
            <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.top" />
          </H4>
          <Body textAlign="center" maxWidth="100%" marginBottom={3}>
            <FormattedMessage
              id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.applause"
              values={{businessName}}
            />
          </Body>
          {isCopied && (
            <Alert
              type={AlertTypes.success}
              title="admin.components.dashboard.reporting.recognition.modal.alert"
              onHide={() => setIsCopied(false)}
            />
          )}
          <H4 textAlign="center" maxWidth="100%" marginBottom={3}>
            <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.giving" />
          </H4>
          <Loading load={receiversLoad}>
            <Flex justifyContent="center">
              {givers.length > 0 ? (
                <Flex gap={2}>
                  {givers.map((u, i) => (
                    <CelebratedUser celebratedUser={u} index={i} />
                  ))}
                </Flex>
              ) : (
                <Body>
                  <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.empty" />
                </Body>
              )}
            </Flex>
          </Loading>
          <H4 textAlign="center" maxWidth="100%" marginTop={3} marginBottom={3}>
            <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.receiving" />
          </H4>
          <Loading load={giversLoad}>
            <Flex justifyContent="center">
              {receivers.length > 0 ? (
                <Flex gap={2}>
                  {receivers.map((u, i) => (
                    <CelebratedUser celebratedUser={u} index={i} isReceiver />
                  ))}
                </Flex>
              ) : (
                <Body>
                  <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.empty" />
                </Body>
              )}
            </Flex>
          </Loading>
          <Body maxWidth="100%" textAlign="center" marginTop={2}>
            <FormattedMessage
              id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.joinIn"
              values={{
                link: (
                  <ButtonRouterLink app={apps.RECOGNITION} tertiary to="/polls/create">
                    <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.here" />
                  </ButtonRouterLink>
                )
              }}
            />
          </Body>
          <Flex justifyContent="space-between" alignItems="center">
            <Button tertiary onClick={() => setShowTopCelebrators(false)}>
              <Icon icon={IconTypes.chevronLeft} marginRight />
              <FormattedMessage id="platform.button.back" />
            </Button>
            <CopyToClipboard text={getTopCelebratorsCopyText()}>
              <Button onClick={handleCopyClick}>
                <FormattedMessage id={copyText} />
              </Button>
            </CopyToClipboard>
          </Flex>
        </div>
      )}
      {!showRecognitionChain && !showTopCelebrators && (
        <>
          <H2 marginBottom={1}>
            <FormattedMessage id={modalTitle} />
          </H2>
          <Body marginBottom={2} maxWidth="100%">
            {hasRewardPoints ? (
              <FormattedMessage
                id="admin.components.dashboard.reporting.recognition.modal.rewardPoints.fullText"
                values={{
                  bold1: (
                    <Body weight="bold" display="inline">
                      {rewardPoints}
                    </Body>
                  ),
                  bold2: (
                    <Body weight="bold" display="inline">
                      <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.rewardPoints.recognising" />
                    </Body>
                  )
                }}
              />
            ) : (
              <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.noRewardPoints.text" />
            )}
          </Body>
          <Flex gap={1}>
            {hasRewardPoints ? (
              <ModalWidget
                title="admin.components.dashboard.reporting.recognition.modal.widgets.reward.title"
                buttonText="admin.components.dashboard.reporting.recognition.modal.widgets.reward.button"
                emoji="🥇"
                onClick={() => adminDashboardTracking.recognitionActions("cta_rewardEmployees")}
                app="wallet"
                link="flexi-assign/recipients"
                text="admin.components.dashboard.reporting.recognition.modal.widgets.reward.text"
                isRecommended
              />
            ) : (
              <ModalWidget
                title="admin.components.dashboard.reporting.recognition.modal.widgets.buyRP.title"
                buttonText="admin.components.dashboard.reporting.recognition.modal.widgets.buyRP.button"
                emoji="💳"
                onClick={() => adminDashboardTracking.recognitionActions("cta_rewardTopup")}
                link="flexi-checkout/top-up"
                app="wallet"
                text="admin.components.dashboard.reporting.recognition.modal.widgets.buyRP.text"
              />
            )}
            <ModalWidget
              title="admin.components.dashboard.reporting.recognition.modal.widgets.share.title"
              buttonText="admin.components.dashboard.reporting.recognition.modal.widgets.share.button"
              emoji="📢"
              onClick={handleShareClick}
              text="admin.components.dashboard.reporting.recognition.modal.widgets.share.text"
            />
            <ModalWidget
              title="admin.components.dashboard.reporting.recognition.modal.widgets.chain.title"
              buttonText="admin.components.dashboard.reporting.recognition.modal.widgets.chain.button"
              emoji="🏆"
              onClick={handleRecognitionChainClick}
              text="admin.components.dashboard.reporting.recognition.modal.widgets.chain.text"
              isRecommended={!hasRewardPoints}
            />
          </Flex>
        </>
      )}
    </GenericModal>
  )
}

RecognitionModal.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default injectIntl(RecognitionModal)
